// Spor: telefonkort: hver figur på kortet (hændelser, montørhold) står ved sin egen ting, aldrig oven i
// en anden nodes hus, et navn eller en anden figur. Laget tegner figuren ved sit anker; her findes den
// mindste forskydning, der står frit, og den holdes, så længe den er fri (ingen dans).

import type { P } from './geo';

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Noget, figurerne skal gå uden om. Et hus eller en nodes ting må dække sin egen ejers figur. */
export interface Blocker {
  owner: string;
  kind: 'house' | 'label' | 'object';
  box: Box;
}

/** En figur, der kan skubbes: dens dele (med den nuværende forskydning), og forskydningen. */
export interface Piece {
  owner: string;
  boxes: Box[];
  /** Forskydningen, der er tegnet lige nu (px). */
  nudge: P;
  /** Målet fra sidste billede: prøves først, så figuren ikke hopper. */
  target: P;
}

/** Overlap, der tæller: mere end en pixel ind og mere end en lille hjørneflis. */
function hits(a: Box, b: Box): boolean {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 1 && h > 1 && w * h > 16;
}

function area(a: Box, b: Box): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

const DIRS = 12;

/**
 * Find hver figurs forskydning. `step` er afstanden mellem ringene (px), `rings` hvor mange der prøves.
 * Figurerne lægges i rækkefølge; en placeret figur er selv en forhindring for de næste.
 */
export function declutter(pieces: Piece[], blockers: Blocker[], step: number, rings: number, bounds: Box, stuck?: string[]): P[] {
  const offsets: P[] = [{ x: 0, y: 0 }];
  for (let r = 1; r <= rings; r++) {
    // Retningerne bliver flere, jo længere ude ringen er, så punkterne står omtrent en ring fra
    // hinanden: med tolv faste retninger var der 40 px mellem dem i ring ti, og en figur i en tæt
    // klynge (Aalborg, Vester Hassing, Brønderslev på bane 5) sprang den frie lomme over.
    const dirs = Math.max(DIRS, 2 * Math.ceil((Math.PI * r) / 2));
    for (let i = 0; i < dirs; i++) {
      // Først op og til siderne (væk fra navnet under huset), så resten.
      const a = -Math.PI / 2 + (i % 2 ? 1 : -1) * Math.ceil(i / 2) * ((Math.PI * 2) / dirs);
      offsets.push({ x: Math.cos(a) * step * r, y: Math.sin(a) * step * r });
    }
  }
  const first = place(pieces.map((_, i) => i), pieces, blockers, offsets, bounds);
  let best = first;
  if (first.stuck.length) {
    // Som navnene: de figurer, der ikke fandt en fri plads, vælger først i en runde til (T15 ved
    // Randers på bane 4 stod fast, fordi naboens figur havde taget den eneste lomme, den selv kunne
    // bruge). Den runde med færrest fastlåste vinder.
    const lost = new Set(first.stuck);
    const order = [...pieces.keys()].sort((a, b) => Number(lost.has(b)) - Number(lost.has(a)) || a - b);
    const second = place(order, pieces, blockers, offsets, bounds);
    if (second.stuck.length < first.stuck.length) best = second;
  }
  for (const i of best.stuck) stuck?.push((pieces[i] as Piece).owner);
  return best.out;
}

/** Figurens kasser, hvis den stod med forskydningen `o` (kasserne er tegnet med `nudge`). */
export function boxesAt(pc: Piece, o: P): Box[] {
  return pc.boxes.map((b) => ({ x: b.x - pc.nudge.x + o.x, y: b.y - pc.nudge.y + o.y, w: b.w, h: b.h }));
}

/**
 * Står figuren frit med forskydningen `o`: uden om de andres huse, alle navne, andres ting og
 * kasserne i `others` (de andre figurer, hvor de skal stå). Scenen spørger, før figuren glider et
 * stykke: er vejen spærret, træder den direkte på plads i stedet for at glide hen over et hus.
 */
export function clearAt(pc: Piece, o: P, blockers: Blocker[], others: Box[]): boolean {
  for (const b of boxesAt(pc, o)) {
    for (const bl of blockers) if ((bl.kind === 'label' || bl.owner !== pc.owner) && hits(b, bl.box)) return false;
    for (const ob of others) if (hits(b, ob)) return false;
  }
  return true;
}

/** Én grådig runde i rækkefølgen `order` (indeks i `pieces`). En placeret figur spærrer for de næste. */
function place(order: number[], pieces: Piece[], blockers: Blocker[], offsets: P[], bounds: Box): { out: P[]; stuck: number[] } {
  const placed: Blocker[] = [];
  const out: P[] = pieces.map(() => ({ x: 0, y: 0 }));
  const stuck: number[] = [];
  for (const idx of order) {
    const pc = pieces[idx] as Piece;
    const base = pc.boxes.map((b) => ({ x: b.x - pc.nudge.x, y: b.y - pc.nudge.y, w: b.w, h: b.h }));
    const at = (o: P): Box[] => base.map((b) => ({ x: b.x + o.x, y: b.y + o.y, w: b.w, h: b.h }));
    const inside = (bs: Box[]): boolean => bs.every((b) => b.x >= bounds.x && b.y >= bounds.y && b.x + b.w <= bounds.x + bounds.w && b.y + b.h <= bounds.y + bounds.h);
    const blocks = (bl: Blocker): boolean => bl.kind === 'label' || bl.owner !== pc.owner;
    const free = (o: P): boolean => {
      const bs = at(o);
      if (!inside(bs)) return false;
      for (const b of bs) {
        for (const bl of blockers) if (blocks(bl) && hits(b, bl.box)) return false;
        for (const bl of placed) if (hits(b, bl.box)) return false;
      }
      return true;
    };
    let best: P | null = null;
    if ((pc.target.x !== 0 || pc.target.y !== 0) && free(pc.target)) {
      // Det gamle sted er stadig frit; men kan figuren stå hjemme igen, går den hjem.
      best = free({ x: 0, y: 0 }) ? { x: 0, y: 0 } : pc.target;
    }
    if (!best) {
      for (const o of offsets) {
        if (free(o)) {
          best = o;
          break;
        }
      }
    }
    if (!best) {
      stuck.push(idx);
      // Intet er helt frit: det sted, der dækker mindst.
      let low = Infinity;
      for (const o of offsets) {
        let sum = 0;
        for (const b of at(o)) {
          for (const bl of blockers) if (blocks(bl)) sum += area(b, bl.box);
          for (const bl of placed) sum += area(b, bl.box);
        }
        if (sum < low - 1) {
          low = sum;
          best = o;
        }
      }
    }
    const o = best ?? { x: 0, y: 0 };
    out[idx] = o;
    for (const b of at(o)) placed.push({ owner: pc.owner, kind: 'object', box: b });
  }
  return { out, stuck };
}
